import { SubmittedWaiver } from '@types';
import { PDFViewerModal } from '_components/atoms';
import UserService from '_services/UserService';
import HelperMethods from '_utils/HelperMethods';
import React, { useEffect, useState } from 'react';
import Barcode from 'react-barcode';

const MySubmittedWaivers: React.FC = () => {
  const [waivers, setWaivers] = useState<SubmittedWaiver[]>([]);
  const [loading, setLoading] = useState(true);
  const [pdfViewerModal, setPdfViewerModal] = useState<{
    isOpen: boolean;
    url: string;
    title: string;
  }>({
    isOpen: false,
    url: '',
    title: '',
  });

  const fetchWaivers = async () => {
    try {
      setLoading(true);

      const responseData =
        (await UserService.getCustomerWaivers()) as unknown as SubmittedWaiver[];

      if (responseData) {
        setWaivers(responseData);
      }
    } catch (error) {
      console.error('Error fetching waivers:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWaivers();
  }, []);

  const openPdfViewer = (url: string, title: string) => {
    setPdfViewerModal({
      isOpen: true,
      url,
      title,
    });
  };

  const closePdfViewer = () => {
    setPdfViewerModal({
      isOpen: false,
      url: '',
      title: '',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Waivers</h1>
          <p className="mt-1 text-sm text-gray-600">
            View your submitted waivers and their status
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <div className="text-sm text-gray-600">
            Total Waivers:{' '}
            <span className="font-semibold">{waivers.length}</span>
          </div>
        </div>
      </div>

      {/* Waivers Table - Desktop */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Barcode
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Skater Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Signed By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {waivers.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    {loading ? 'Loading...' : 'No waivers found'}
                  </td>
                </tr>
              ) : (
                waivers.map(waiver => (
                  <tr key={waiver.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col items-center">
                        <div className="mb-2">
                          <Barcode
                            value={waiver.barcodeId}
                            width={1}
                            height={50}
                            fontSize={10}
                            format="CODE128"
                          />
                        </div>
                        <div className="text-xs text-gray-500 text-center">
                          {waiver.barcodeId}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {waiver.skaterName}
                      </div>
                      <div className="text-sm text-gray-500">
                        DOB: {HelperMethods.formatDate(waiver.dateOfBirth)}
                      </div>
                      <div className="text-xs text-gray-400">
                        {waiver.isAdult ? 'Adult' : 'Minor'}
                      </div>
                      <div className="text-xs text-gray-400">
                        Submitted:{' '}
                        {HelperMethods.formatDateTimeLocal(waiver.submittedAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {waiver.email}
                      </div>
                      <div className="text-sm text-gray-500">
                        {waiver.phoneNumber}
                      </div>
                      <div className="text-xs text-gray-400">
                        Emergency: {waiver.emergencyContactPhone}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {waiver.signedBy}
                      </div>
                      <div className="text-sm text-gray-500">
                        {waiver.relationship}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col space-y-1">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            waiver.accepted
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {waiver.accepted ? 'Accepted' : 'Not Accepted'}
                        </span>
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            waiver.hasReadWaiver
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {waiver.hasReadWaiver ? 'Read' : 'Not Read'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2 justify-center">
                        {waiver.pdfFilePath ? (
                          <button
                            onClick={() =>
                              openPdfViewer(
                                waiver.pdfFilePath,
                                `Waiver - ${waiver.skaterName}`,
                              )
                            }
                            className="text-blue-600 hover:text-blue-900"
                          >
                            View Waiver
                          </button>
                        ) : (
                          <div className="text-gray-500">Needs Waiver</div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Waivers Cards - Mobile */}
      <div className="md:hidden space-y-4">
        {waivers.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center text-gray-500">
            {loading ? 'Loading...' : 'No waivers found'}
          </div>
        ) : (
          waivers.map(waiver => (
            <div
              key={waiver.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
            >
              {/* Barcode Info */}
              <div className="mb-4 flex flex-col items-center p-3 bg-gray-50 rounded-md">
                <div className="mb-2">
                  <Barcode
                    value={waiver.barcodeId}
                    width={1}
                    height={50}
                    fontSize={10}
                    format="CODE128"
                  />
                </div>
                <div className="text-xs text-gray-500 text-center">
                  {waiver.barcodeId}
                </div>
              </div>

              {/* Skater Info */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {waiver.skaterName}
                  </h3>
                  <div className="flex flex-col space-y-1">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        waiver.accepted
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {waiver.accepted ? 'Accepted' : 'Not Accepted'}
                    </span>
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        waiver.hasReadWaiver
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {waiver.hasReadWaiver ? 'Read' : 'Not Read'}
                    </span>
                  </div>
                </div>
                <div className="text-sm text-gray-600">
                  DOB: {HelperMethods.formatDate(waiver.dateOfBirth)}
                </div>
                <div className="text-xs text-gray-400">
                  {waiver.isAdult ? 'Adult' : 'Minor'}
                </div>
                <div className="text-xs text-gray-400">
                  Submitted:{' '}
                  {HelperMethods.formatDateTimeLocal(waiver.submittedAt)}
                </div>
              </div>

              {/* Contact Info */}
              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  Contact Details
                </h4>
                <div className="text-sm text-gray-600">
                  <div className="font-medium">{waiver.email}</div>
                  <div>{waiver.phoneNumber}</div>
                  <div className="text-xs text-gray-500">
                    Emergency: {waiver.emergencyContactPhone}
                  </div>
                </div>
              </div>

              {/* Signed By Info */}
              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  Signed By
                </h4>
                <div className="text-sm text-gray-600">
                  <div className="font-medium">{waiver.signedBy}</div>
                  <div>{waiver.relationship}</div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                {waiver.pdfFilePath && (
                  <button
                    onClick={() =>
                      openPdfViewer(
                        waiver.pdfFilePath,
                        `Waiver - ${waiver.skaterName}`,
                      )
                    }
                    className="inline-flex items-center px-3 py-2 bg-blue-100 text-blue-700 text-sm rounded-md hover:bg-blue-200 transition-colors"
                  >
                    View Waiver
                  </button>
                )}
              </div>

              {/* Minors Info */}
              {waiver.minors && waiver.minors.trim() !== '' && (
                <div className="mt-3 p-3 bg-yellow-50 rounded-md">
                  <h4 className="text-sm font-medium text-yellow-800 mb-1">
                    Associated Minors
                  </h4>
                  <div className="text-xs text-yellow-700">{waiver.minors}</div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* PDF Viewer Modal */}
      <PDFViewerModal
        isOpen={pdfViewerModal.isOpen}
        onClose={closePdfViewer}
        url={pdfViewerModal.url}
        title={pdfViewerModal.title}
      />
    </div>
  );
};

export default MySubmittedWaivers;
